// Maaş hesapları (Çalışan Finans Yönetimi ve Maaş Ödemeleri). Fonksiyonlar
// eski AddSalaryModal / ZamModal / SalaryPaymentPage içindeki hesapların
// aynısıdır; oranlar değişmedi.

/** Net from gross with the income tax brackets (used to find gross from net). */
export const calculateCosts = (grossSalary) => {
  const sskEmployeeShare = grossSalary * 0.14; // %14
  const unemploymentEmployeeShare = grossSalary * 0.01; // %1
  const stampTax = grossSalary * 0.00759; // %0.759

  const incomeTaxBase = grossSalary - (sskEmployeeShare + unemploymentEmployeeShare);
  let incomeTax = 0;

  if (incomeTaxBase <= 24000) {
    incomeTax = incomeTaxBase * 0.15;
  } else if (incomeTaxBase <= 53000) {
    incomeTax = 3600 + (incomeTaxBase - 24000) * 0.20;
  } else if (incomeTaxBase <= 190000) {
    incomeTax = 9400 + (incomeTaxBase - 53000) * 0.27;
  } else {
    incomeTax = 46390 + (incomeTaxBase - 190000) * 0.35;
  }

  const totalDeductions = sskEmployeeShare + unemploymentEmployeeShare + incomeTax + stampTax;
  const netSalary = grossSalary - totalDeductions;

  const sskEmployerShare = grossSalary * 0.155; // %15.5
  const unemploymentEmployerShare = grossSalary * 0.02; // %2
  const totalEmployerCost = grossSalary + sskEmployerShare + unemploymentEmployerShare;

  return {
    netSalary: netSalary.toFixed(2),
    totalDeductions: totalDeductions.toFixed(2),
    totalEmployerCost: totalEmployerCost.toFixed(2),
  };
};

/**
 * Gross salary for a wanted net (binary search, as before). `empty` is what
 * the fields get for an empty / zero net: '' in the salary form, 0 in the
 * bulk raise (the two old copies differed only there).
 */
export const calculateGrossFromNet = (targetNet, { empty = '' } = {}) => {
  if (!targetNet || targetNet <= 0) {
    return {
      grossSalary: empty,
      totalDeductions: empty,
      totalEmployerCost: empty,
    };
  }

  let low = targetNet; // Minimum possible gross (net amount)
  let high = targetNet * 2; // Maximum possible gross (reasonable upper limit)
  let grossSalary = (low + high) / 2;
  let iterations = 0;
  const maxIterations = 30; // Prevent infinite loops

  while (iterations < maxIterations) {
    const calculations = calculateCosts(grossSalary);
    const currentNet = parseFloat(calculations.netSalary);

    // If we're within 0.01 TL, we've found our answer
    if (Math.abs(currentNet - targetNet) < 0.01) {
      return {
        grossSalary: grossSalary.toFixed(2),
        totalDeductions: calculations.totalDeductions,
        totalEmployerCost: calculations.totalEmployerCost,
      };
    }

    // Binary search adjustment
    if (currentNet > targetNet) {
      high = grossSalary;
    } else {
      low = grossSalary;
    }

    grossSalary = (low + high) / 2;
    iterations++;
  }

  // Return best approximation if exact match not found
  const finalCalculations = calculateCosts(grossSalary);
  return {
    grossSalary: grossSalary.toFixed(2),
    totalDeductions: finalCalculations.totalDeductions,
    totalEmployerCost: finalCalculations.totalEmployerCost,
  };
};

/**
 * The simple breakdown the salary tables and the monthly payments use (fixed
 * rates, no income tax), from the gross salary.
 */
export const simpleBreakdown = (gross) => {
  const grossSalary = Number(gross) || 0;
  const sskEmployeeShare = grossSalary * 0.14;
  const unemploymentEmployeeShare = grossSalary * 0.01;
  const stampTax = grossSalary * 0.00759;
  const totalDeductions = sskEmployeeShare + unemploymentEmployeeShare + stampTax;
  const netSalary = grossSalary - totalDeductions;
  const sskEmployerShare = grossSalary * 0.155;
  const unemploymentEmployerShare = grossSalary * 0.02;
  const totalEmployerCost = grossSalary + sskEmployerShare + unemploymentEmployerShare;
  return {
    grossSalary,
    sskEmployeeShare,
    unemploymentEmployeeShare,
    stampTax,
    totalDeductions,
    netSalary,
    sskEmployerShare,
    unemploymentEmployerShare,
    totalEmployerCost,
  };
};

/** "12.345,67 ₺" (the old pages' format). */
export const formatTry = (amount) => `${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Number(amount) || 0).replace('₺', '').trim()} ₺`;
